import * as Sentry from '@sentry/react';
import { supabase } from '../supabase';
import { getPendingLogs, removePendingLog, updatePendingLogSessionIds } from '../utils/offlineQueue';

// A workout day opened with no connection has no workout_sessions row yet, so its sets are queued
// under a temporary id and re-pointed at the real session once one can be created.
//
// Every exercise on the day is its own WorkoutLogger, and they all mount together. Each used to
// make its own temporary id and write it to the same localStorage entry, so only the last one
// survived: on reconnect only that exercise's sets were re-pointed. The others kept an id that is
// not a number, which the database refuses (workout_logs.session_id is a bigint), so they stayed
// in the queue for good. Everything here exists so that one day has ONE temporary id.

export const OFFLINE_SESSION_PREFIX = 'offline-';
const PENDING_KEY_PREFIX = 'atone_pending_session:';
const LOG_CONFLICT = 'session_id,program_exercise_id,set_number';
// A sync asked for while one is running is run once more afterwards. Bounded, so a caller that
// keeps asking cannot hold the loop open.
const MAX_PASSES = 3;

export const isOfflineSessionId = (id) => typeof id === 'string' && id.startsWith(OFFLINE_SESSION_PREFIX);

// How long opening a workout day waits for the server before carrying on without it.
//
// The browser can say it is online on a signal too weak to carry anything, and a request then
// hangs rather than fails. Worse, once the login token has expired (an hour in the background is
// enough) the Supabase client first spends about 25 seconds trying to refresh it before each
// request fails, and opening a day makes three requests in a row. Without a limit every exercise
// sat on "Loading..." for minutes.
let lookupTimeLimitMs = 10000;

/** Resolves or rejects with the promise, or rejects with { timedOut: true } after the limit. */
export const withTimeLimit = (promise, ms = lookupTimeLimitMs) => new Promise((resolve, reject) => {
  const timer = setTimeout(
    () => reject(Object.assign(new Error('No answer from the server'), { timedOut: true })),
    ms
  );
  Promise.resolve(promise).then(
    (value) => { clearTimeout(timer); resolve(value); },
    (error) => { clearTimeout(timer); reject(error); }
  );
});

/** Tests only. */
export const _setLookupTimeLimitForTests = (ms) => { lookupTimeLimitMs = ms; };

const isLogItem = (item) => item.type !== 'session_complete' && !!item.logData;
const dayKey = ({ userId, programId, dayId }) => `${userId}:${programId}:${dayId}`;

// Temporary ids given out since this page loaded, by day. Also what keeps a day on one id when
// localStorage cannot be written.
const handedOut = new Map(); // dayKey -> { tempId, userId, programId, dayId, assignedAt }
// Temporary ids that now have a real session.
const reconciled = new Map(); // tempId -> realId
const listeners = new Set();
let inFlight = null;
let askedAgain = false;
let reported = false;

const newTempId = () => {
  const unique = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${OFFLINE_SESSION_PREFIX}${unique}`;
};

const readStored = (lsKey) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(lsKey));
    return parsed && isOfflineSessionId(parsed.tempId) ? { ...parsed, lsKey } : null;
  } catch {
    return null;
  }
};

/**
 * The temporary session id for this day. Every caller for the same day gets the same one,
 * including after a refresh while still offline.
 */
export const getOfflineSessionId = ({ userId, programId, dayId, assignedAt }) => {
  const day = { userId, programId, dayId };
  const key = dayKey(day);
  const known = handedOut.get(key);
  if (known) return known.tempId;

  const lsKey = `${PENDING_KEY_PREFIX}${key}`;
  const stored = readStored(lsKey);
  const entry = stored
    ? { ...stored, assignedAt: assignedAt ?? stored.assignedAt }
    : { ...day, assignedAt, tempId: newTempId(), lsKey };
  if (!stored) {
    try {
      localStorage.setItem(lsKey, JSON.stringify({ tempId: entry.tempId, userId, programId, dayId, assignedAt }));
    } catch {
      // Private mode or a full store: the in-memory copy below still keeps the day on one id.
    }
  }
  handedOut.set(key, entry);
  return entry.tempId;
};

/** The id to save under: the real session if this temporary id has been reconciled, else itself. */
export const liveSessionId = (id) => reconciled.get(id) ?? id;

/** Called with { tempId, realId } when a temporary id gets its real session. Returns an unsubscribe. */
export const onSessionReconciled = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const pendingEntries = () => {
  const byTempId = new Map();
  try {
    for (const lsKey of Object.keys(localStorage)) {
      if (!lsKey.startsWith(PENDING_KEY_PREFIX)) continue;
      const entry = readStored(lsKey);
      if (entry) byTempId.set(entry.tempId, entry);
    }
  } catch {
    // localStorage unavailable: fall through to what this page handed out
  }
  for (const entry of handedOut.values()) {
    if (!byTempId.has(entry.tempId)) byTempId.set(entry.tempId, entry);
  }
  return [...byTempId.values()];
};

export const hasPendingOfflineSessions = () => pendingEntries().length > 0;

const isInUse = (entry) => handedOut.get(dayKey(entry))?.tempId === entry.tempId;

const forget = (entry) => {
  try {
    if (entry.lsKey) localStorage.removeItem(entry.lsKey);
  } catch {
    // nothing to clean
  }
  if (isInUse(entry)) handedOut.delete(dayKey(entry));
};

const runOnce = async ({ resolveSession }) => {
  // ── 1. Give each offline-started day its real session ──
  const queued = (await getPendingLogs()).filter(isLogItem);
  const waitedOn = new Set(queued.map(item => item.logData.session_id).filter(isOfflineSessionId));
  const resolvedNow = [];
  let dayAlreadyCompleted = 0;
  // Set when a request got no answer in time. Only one sync runs at a time for the whole page, so
  // a request that hangs must not be waited on for ever, or nothing would sync again until the
  // page was reloaded. One silence is taken as the connection being down and the run stops there.
  let noAnswer = false;

  for (const entry of pendingEntries()) {
    // Left over from an earlier visit with nothing queued under it. Resolving it would create a
    // session for a day nobody is training, so it is dropped instead.
    if (!waitedOn.has(entry.tempId) && !isInUse(entry)) {
      forget(entry);
      continue;
    }
    if (noAnswer) break;
    try {
      const { sessionId: realId, alreadyCompleted } =
        await withTimeLimit(resolveSession(entry.userId, entry.programId, entry.dayId, entry.assignedAt));
      if (!realId) {
        // The day was finished some other way. Its queued sets are kept, not attached and not
        // deleted: writing them would overwrite a completed workout.
        if (alreadyCompleted) dayAlreadyCompleted++;
        continue;
      }
      // From here on, anything saved under the temporary id goes to the real session, including a
      // set that is being queued at this very moment.
      reconciled.set(entry.tempId, realId);
      resolvedNow.push({ ...entry, realId });
      listeners.forEach((listener) => {
        try { listener({ tempId: entry.tempId, realId }); } catch { /* one logger must not stop the rest */ }
      });
    } catch (error) {
      // Still offline, or the lookup failed. The entry stays and the next sync retries.
      if (error?.timedOut) noAnswer = true;
    }
  }

  // ── 2. Upload the queued sets ──
  const toSync = (await getPendingLogs()).filter(isLogItem);
  let synced = 0;
  const unmatched = [];
  for (const item of toSync) {
    const sessionId = liveSessionId(item.logData.session_id);
    // No real session to put it in. Sent as it is, the database would refuse a temporary id, and
    // would accept a missing one as a set belonging to no workout at all.
    if (sessionId == null || isOfflineSessionId(sessionId)) {
      unmatched.push(sessionId);
      continue;
    }
    if (noAnswer) continue; // stays queued
    try {
      const { error } = await withTimeLimit(supabase
        .from('workout_logs')
        .upsert({ ...item.logData, session_id: sessionId }, { onConflict: LOG_CONFLICT }));
      if (!error) {
        await removePendingLog(item.id);
        synced++;
      }
    } catch (error) {
      // Stays queued — the next sync retries
      if (error?.timedOut) noAnswer = true;
    }
  }

  // ── 3. Make the new ids survive a refresh, then let go of the temporary ones ──
  // Anything that could not be uploaded just now must carry the real id in the queue itself,
  // because the record of which temporary id meant which day is about to be removed.
  for (const entry of resolvedNow) {
    try {
      await updatePendingLogSessionIds(entry.tempId, entry.realId);
      forget(entry);
    } catch {
      // Queue not writable: keep the entry, so the next sync resolves the same session again.
    }
  }

  // Sets that no later sync can place: no session id at all, or a temporary id nothing remembers.
  const stillPending = new Set(pendingEntries().map(entry => entry.tempId));
  const orphaned = unmatched.filter(id => id == null || !stillPending.has(id)).length;
  if ((orphaned > 0 || dayAlreadyCompleted > 0) && !reported) {
    reported = true;
    Sentry.captureMessage('Offline workout sets could not be matched to a session', {
      level: 'warning',
      extra: { orphaned, dayAlreadyCompleted, queued: toSync.length },
    });
  }

  return { synced, remaining: toSync.length - synced, unmatched: unmatched.length };
};

/**
 * Reconciles offline-started sessions and uploads the queued sets. One run at a time for the
 * whole page: every WorkoutLogger on the day asks on reconnect, and they all get the same result.
 *
 * resolveSession(userId, programId, dayId, assignedAt) -> { sessionId, alreadyCompleted }
 */
export const syncOfflineWorkout = (deps) => {
  if (inFlight) {
    askedAgain = true;
    return inFlight;
  }
  inFlight = (async () => {
    let result = { synced: 0, remaining: 0, unmatched: 0 };
    for (let pass = 0; pass < MAX_PASSES; pass++) {
      askedAgain = false;
      const once = await runOnce(deps);
      result = { synced: result.synced + once.synced, remaining: once.remaining, unmatched: once.unmatched };
      if (!askedAgain) break;
    }
    return result;
  })().finally(() => { inFlight = null; askedAgain = false; });
  return inFlight;
};

/** Tests only: forget everything this page has handed out or reconciled. */
export const _resetOfflineSessionState = () => {
  handedOut.clear();
  reconciled.clear();
  listeners.clear();
  inFlight = null;
  askedAgain = false;
  reported = false;
};
